import React, { useCallback, useEffect, useState } from 'react';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { SharedStyles as Shared } from '@screens/Authenticated/Property/sharedStyles';
import { Styles as FormStyles } from '@screens/Authenticated/Property/NewRegisterScreen/style';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CircularProgress, Snackbar, Alert } from '@mui/material';

type RowDraft = {
  _key: string;
  id: number | null;
  titulo: string;
  active: boolean;
};

let _tempCounter = 0;
const newTempKey = () => `new-${++_tempCounter}`;

interface SortableRowProps {
  row: RowDraft;
  onChange: (
    key: string,
    field: 'titulo' | 'active',
    value: string | boolean
  ) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ row, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: row._key });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    background: isDragging ? '#f0f7ff' : '#ffffff',
    borderBottom: '1px solid #ececec'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    padding: '2px 0',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.15s'
  };

  return (
    <tr ref={setNodeRef} style={rowStyle}>
      <td
        style={{
          width: 36,
          textAlign: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: '#bbb',
          fontSize: 18,
          userSelect: 'none',
          paddingTop: 10,
          paddingBottom: 10
        }}
        {...attributes}
        {...listeners}>
        ⠿
      </td>

      <td style={{ padding: '8px' }}>
        <input
          value={row.titulo}
          onChange={(e) => onChange(row._key, 'titulo', e.target.value)}
          placeholder="ex.: Venda"
          style={inputStyle}
          onFocus={(e) => (e.currentTarget.style.borderBottomColor = '#125dae')}
          onBlur={(e) =>
            (e.currentTarget.style.borderBottomColor = 'transparent')
          }
        />
      </td>

      <td style={{ width: 80, textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={row.active}
          onChange={(e) => onChange(row._key, 'active', e.target.checked)}
          style={{
            width: 16,
            height: 16,
            cursor: 'pointer',
            accentColor: '#125dae'
          }}
        />
      </td>
    </tr>
  );
};

const ConfigConditionsScreen: React.FC = () => {
  const { adminCondicoesImovel, fetchAllCondicoesImovel, syncCondicoesImovel } =
    useMetadataStore();

  const [rows, setRows] = useState<RowDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchAllCondicoesImovel();
      setLoading(false);
    })();
  }, [fetchAllCondicoesImovel]);

  useEffect(() => {
    const sorted = [...adminCondicoesImovel].sort(
      (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
    );
    setRows(
      sorted.map((item) => ({
        _key: String(item.id),
        id: item.id,
        titulo: item.titulo,
        active: item.active
      }))
    );
  }, [adminCondicoesImovel]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((prev) => {
      const oldIdx = prev.findIndex((r) => r._key === String(active.id));
      const newIdx = prev.findIndex((r) => r._key === String(over.id));
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const handleChange = useCallback(
    (key: string, field: 'titulo' | 'active', value: string | boolean) => {
      setRows((prev) =>
        prev.map((r) => (r._key === key ? { ...r, [field]: value } : r))
      );
    },
    []
  );

  const handleAdd = () => {
    setRows((prev) => [
      ...prev,
      { _key: newTempKey(), id: null, titulo: '', active: true }
    ]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = rows
        .filter((r) => r.titulo.trim() !== '')
        .map((r) => ({ id: r.id, titulo: r.titulo.trim(), active: r.active }));
      const ok = await syncCondicoesImovel(items);
      if (!ok) throw new Error('sync failed');
      await fetchAllCondicoesImovel();
      setSuccessMsg('Condições salvas com sucesso!');
    } catch {
      setErrorMsg('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <Shared.PageSection>
        <h1>Condições de Imóvel</h1>
        <Shared.RectangleBar />

        {loading ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 14,
                background: '#fff',
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
              }}>
              <thead>
                <tr
                  style={{
                    background: '#f5f5f5',
                    borderBottom: '2px solid #D9D9D9',
                    textAlign: 'left',
                    color: '#5D5F64',
                    fontWeight: 600
                  }}>
                  <th style={{ width: 36 }} />
                  <th style={{ padding: '12px 8px' }}>Título</th>
                  <th
                    style={{
                      width: 80,
                      padding: '12px 8px',
                      textAlign: 'center'
                    }}>
                    Ativo
                  </th>
                </tr>
              </thead>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}>
                <SortableContext
                  items={rows.map((r) => r._key)}
                  strategy={verticalListSortingStrategy}>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          style={{
                            padding: '24px',
                            textAlign: 'center',
                            color: '#aaa'
                          }}>
                          Nenhuma condição cadastrada. Clique em Adicionar.
                        </td>
                      </tr>
                    )}
                    {rows.map((row) => (
                      <SortableRow
                        key={row._key}
                        row={row}
                        onChange={handleChange}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 12,
            marginTop: 24,
            justifyContent: 'flex-end'
          }}>
          <FormStyles.Button type="button" onClick={handleAdd}>
            Adicionar
          </FormStyles.Button>
          <FormStyles.Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Salvando…' : 'SALVAR'}
          </FormStyles.Button>
        </div>
      </Shared.PageSection>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={5000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </AuthenticatedLayout>
  );
};

export default ConfigConditionsScreen;
